import { extensionApiRef } from '@agilelab/plugin-wb-builder-catalog';
import {
  ComponentInstanceEntities,
  ComponentInstanceEntity,
  GET_OUTPUT_PORTS_BY_INSTANCE_ID,
} from '@agilelab/plugin-wb-marketplace-common';
import {
  compactStringRef,
  expandEntityRef,
  GroupEntityRef,
  UserEntityRef,
} from '@agilelab/plugin-wb-platform-common';
import { gql, useQuery } from '@apollo/client';
import { useApi } from '@mainstage/core-plugin-api';
import { isEqual } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { SystemContext } from './DataProductContext';
import { useGetPolicyViolationsCountMapById } from './useGetPolicyViolationByIdHook';

export const useOutputPorts = (
  username: string,
  selectedOutputportIds?: number[],
) => {
  const catalogApi = useApi(extensionApiRef);
  const [selectedOutputPorts, setSelectedOutputPorts] = useState<
    ComponentInstanceEntity[]
  >([]);
  const dpInstance = useContext(SystemContext);
  const {
    value: groupsHierarchy,
    loading: groupsHierarchyLoading,
    error: groupsHierarchyError,
  } = useAsync(async () => {
    const userEntityRef = expandEntityRef(username) as UserEntityRef;
    const resolvedGroupsHierarchy: (GroupEntityRef | UserEntityRef)[] =
      await catalogApi.getGroupsAncestors(userEntityRef);

    resolvedGroupsHierarchy.push(userEntityRef);

    // WIT-5372 - this is needed to cover both compact and expanded refs in ACLs due to a bug
    return [
      ...resolvedGroupsHierarchy,
      ...resolvedGroupsHierarchy.map(compactStringRef),
    ];
  }, [catalogApi, username]);

  const { data: policyViolationsCountMap } = useGetPolicyViolationsCountMapById(
    dpInstance?.external_id,
    dpInstance?.environment.name,
  );

  const {
    data: opsWithACLs,
    loading: opsLoading,
    error: opsError,
  } = useQuery<ComponentInstanceEntities>(
    gql(GET_OUTPUT_PORTS_BY_INSTANCE_ID),
    {
      variables: {
        id: dpInstance.id,
        identities: groupsHierarchy ?? [],
      },
      skip: !groupsHierarchy,
    },
  );

  const outputPorts = useMemo(() => {
    if (opsWithACLs && opsWithACLs?.instances && policyViolationsCountMap) {
      return opsWithACLs.instances.map(op => {
        const policyViolationCount =
          policyViolationsCountMap?.aggregatedViolations.get(op.external_id);
        return {
          ...op,
          components: op.components?.map(c => {
            const policyViolationCountSub =
              policyViolationsCountMap?.aggregatedViolations.get(
                c.data.external_id,
              );
            return {
              data: {
                ...c.data,
                policyViolationCount: policyViolationCountSub,
              },
            };
          }),
          _policyViolationCount: policyViolationCount ?? new Map(),
        };
      });
    }
    return opsWithACLs?.instances || [];
  }, [opsWithACLs, policyViolationsCountMap]);

  useEffect(() => {
    const newFilteredOutputPorts =
      selectedOutputportIds && outputPorts
        ? outputPorts.filter(d => selectedOutputportIds.includes(d.id))
        : [];

    if (!isEqual(newFilteredOutputPorts, selectedOutputPorts)) {
      setSelectedOutputPorts(newFilteredOutputPorts);
    }
  }, [outputPorts, selectedOutputportIds, selectedOutputPorts]);

  return {
    loading:
      groupsHierarchyLoading || opsLoading || !groupsHierarchy || !opsWithACLs,
    error: opsError ?? groupsHierarchyError,
    selectedOutputPorts: selectedOutputPorts,
    allOutputPorts: outputPorts,
    dpInstance,
  };
};
